import apiService from './apiService';

// TypeScript interfaces
export interface VendorBillItem {
  id?: string;
  billId?: string;
  productId?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  taxIds?: string[];
  accountId: string;
  departmentId?: string | null;
  departmentName?: string | null;
  partnerId?: string | null;
  subtotal: number;
  createdAt?: string;
}

export interface VendorBillTax {
  id?: string;
  billId?: string;
  taxId: string;
  taxName: string;
  base: number;
  amount: number;
}

export interface VendorBill {
  id: string;
  billNo: string;
  vendorId: string;
  billDate: string;
  dueDate?: string;
  paymentTermId?: string;
  accountingDate?: string;
  amountUntaxed: number;
  amountTax: number;
  amountTotal: number;
  amountResidual: number;
  state: 'DRAFT' | 'POSTED' | 'PAID' | 'CANCELLED';
  paymentState: 'NOT_PAID' | 'PARTIAL' | 'PAID';
  journalEntryId?: string;
  chequeNo?: string | null;
  invoiceNo?: string | null;
  companyId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  vendor?: {
    id: string;
    name: string;
    code: string;
    email?: string;
    phone?: string;
    address?: string;
    contactPerson?: string;
    paymentTerms?: string;
  };
  items: VendorBillItem[];
  taxes: VendorBillTax[];
  payments?: any[];
}

export interface CreateVendorBillInput {
  billNo?: string;
  vendorId: string;
  billDate: string;
  dueDate?: string;
  paymentTermId?: string;
  accountingDate?: string;
  amountUntaxed: number;
  amountTax: number;
  amountTotal: number;
  chequeNo?: string;
  invoiceNo?: string;
  description?: string;
  items: Omit<VendorBillItem, 'id' | 'billId' | 'createdAt'>[];
  taxes?: Omit<VendorBillTax, 'id' | 'billId'>[];
}

export interface UpdateVendorBillInput {
  vendorId?: string;
  billDate?: string;
  dueDate?: string;
  paymentTermId?: string;
  accountingDate?: string;
  amountUntaxed?: number;
  amountTax?: number;
  amountTotal?: number;
  chequeNo?: string;
  invoiceNo?: string;
  items?: Omit<VendorBillItem, 'id' | 'billId' | 'createdAt'>[];
  taxes?: Omit<VendorBillTax, 'id' | 'billId'>[];
}

export interface VendorBillFilters {
  vendorId?: string;
  state?: string;
  paymentState?: string;
  fromDate?: string;
  toDate?: string;
  searchTerm?: string;
}

export interface PostVendorBillInput {
  accountsPayableAccountId: string;
  expenseAccountId: string;
  journalId: string;
}

export interface VendorBillStats {
  totalBills: number;
  draftBills: number;
  postedBills: number;
  paidBills: number;
  overdueBills: number;
  totalAmountDue: number;
  totalAmountOverdue: number;
}

class VendorBillService {
  private baseUrl = '/accounting/vendor-bills';

  /**
   * Get all vendor bills with optional filters
   */
  async getAllBills(filters?: VendorBillFilters): Promise<VendorBill[]> {
    const params = new URLSearchParams();
    if (filters?.vendorId) params.append('vendorId', filters.vendorId);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.paymentState) params.append('paymentState', filters.paymentState);
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);
    if (filters?.searchTerm) params.append('searchTerm', filters.searchTerm);

    const response: any = await apiService.get(
      `${this.baseUrl}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data || [];
  }

  /**
   * Get vendor bill by ID
   */
  async getBillById(id: string): Promise<VendorBill> {
    const response: any = await apiService.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get vendor bill by bill number
   */
  async getBillByNumber(billNo: string): Promise<VendorBill> {
    const response: any = await apiService.get(`${this.baseUrl}/number/${billNo}`);
    return response.data;
  }

  /**
   * Create a new vendor bill
   */
  async createBill(data: CreateVendorBillInput): Promise<VendorBill> {
    const response: any = await apiService.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update an existing vendor bill
   */
  async updateBill(id: string, data: UpdateVendorBillInput): Promise<VendorBill> {
    const response: any = await apiService.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Post a vendor bill (DRAFT → POSTED)
   */
  async postBill(id: string, data: PostVendorBillInput): Promise<VendorBill> {
    const response: any = await apiService.post(`${this.baseUrl}/${id}/post`, data);
    return response.data;
  }

  /**
   * Mark vendor bill as PAID
   */
  async markBillAsPaid(id: string): Promise<VendorBill> {
    const response: any = await apiService.post(`${this.baseUrl}/${id}/mark-paid`, {});
    return response.data;
  }

  /**
   * Cancel a vendor bill
   */
  async cancelBill(id: string, data: PostVendorBillInput): Promise<VendorBill> {
    const response: any = await apiService.post(`${this.baseUrl}/${id}/cancel`, data);
    return response.data;
  }

  /**
   * Delete a vendor bill
   */
  async deleteBill(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Get vendor bills by vendor
   */
  async getBillsByVendor(vendorId: string): Promise<VendorBill[]> {
    const response: any = await apiService.get(`${this.baseUrl}/vendor/${vendorId}`);
    return response.data || [];
  }

  /**
   * Get overdue vendor bills
   */
  async getOverdueBills(): Promise<VendorBill[]> {
    const response: any = await apiService.get(`${this.baseUrl}/overdue`);
    return response.data || [];
  }

  /**
   * Get bills by payment state
   */
  async getBillsByPaymentState(paymentState: string): Promise<VendorBill[]> {
    const response: any = await apiService.get(`${this.baseUrl}/payment-state/${paymentState}`);
    return response.data || [];
  }

  /**
   * Get vendor bill statistics
   */
  async getStats(): Promise<VendorBillStats> {
    const response: any = await apiService.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  /**
   * Get next bill number
   */
  async getNextBillNo(): Promise<string> {
    const response: any = await apiService.get(`${this.baseUrl}/next-bill-no`);
    return response.data.billNo;
  }

  /**
   * Get AP Aging Report with 30/60/90+ day buckets
   */
  async getAgingReport(asOfDate?: string): Promise<AgingReportData> {
    const params = asOfDate ? `?asOfDate=${asOfDate}` : '';
    const response: any = await apiService.get(`${this.baseUrl}/aging-report${params}`);
    return response.data;
  }

  /**
   * Get Vendor Statement
   */
  async getVendorStatement(vendorId: string, fromDate?: string, toDate?: string): Promise<VendorStatementData> {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response: any = await apiService.get(`/accounting/vendors/${vendorId}/statement${queryString}`);
    return response.data;
  }
}

// AP Aging Report types
export interface AgingReportSummary {
  current: number;
  days30: number;
  days60: number;
  days90Plus: number;
  total: number;
}

export interface AgingReportByVendor {
  vendorId: string;
  vendorName: string;
  vendorCode: string;
  current: number;
  days30: number;
  days60: number;
  days90Plus: number;
  total: number;
}

export interface AgingReportDetail {
  billId: string;
  billNo: string;
  vendorId: string;
  vendorName: string;
  billDate: string;
  dueDate: string | null;
  amountTotal: number;
  amountResidual: number;
  daysOverdue: number;
  agingBucket: 'current' | 'days30' | 'days60' | 'days90Plus';
}

export interface AgingReportData {
  summary: AgingReportSummary;
  byVendor: AgingReportByVendor[];
  details: AgingReportDetail[];
}

// Vendor Statement types
export interface VendorStatementTransaction {
  date: string;
  type: 'BILL' | 'PAYMENT';
  reference: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

export interface VendorStatementData {
  vendor: {
    id: string;
    name: string;
    code: string;
    email: string | null;
    phone: string | null;
    address: string | null;
  };
  period: {
    fromDate: string;
    toDate: string;
  };
  openingBalance: number;
  transactions: VendorStatementTransaction[];
  closingBalance: number;
  summary: {
    totalBills: number;
    totalPayments: number;
    totalDue: number;
  };
}

// Recurring Vendor Bill types
export type RecurringFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export interface RecurringVendorBillItem {
  id?: string;
  recurringBillId?: string;
  productId?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  taxIds?: string[];
  accountId: string;
  subtotal: number;
}

export interface RecurringVendorBill {
  id: string;
  name: string;
  vendorId: string;
  frequency: RecurringFrequency;
  startDate: string;
  endDate?: string;
  nextRunDate: string;
  dayOfMonth?: number;
  dayOfWeek?: number;
  amountUntaxed: number;
  amountTax: number;
  amountTotal: number;
  isActive: boolean;
  lastGeneratedDate?: string;
  generatedCount: number;
  companyId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  vendor?: {
    id: string;
    name: string;
    code: string;
  };
  items: RecurringVendorBillItem[];
}

export interface CreateRecurringVendorBillInput {
  name: string;
  vendorId: string;
  frequency: RecurringFrequency;
  startDate: string;
  endDate?: string;
  dayOfMonth?: number;
  dayOfWeek?: number;
  amountUntaxed: number;
  amountTax: number;
  amountTotal: number;
  items: Omit<RecurringVendorBillItem, 'id' | 'recurringBillId'>[];
}

export interface UpdateRecurringVendorBillInput {
  name?: string;
  vendorId?: string;
  frequency?: RecurringFrequency;
  startDate?: string;
  endDate?: string;
  dayOfMonth?: number;
  dayOfWeek?: number;
  amountUntaxed?: number;
  amountTax?: number;
  amountTotal?: number;
  isActive?: boolean;
  items?: Omit<RecurringVendorBillItem, 'id' | 'recurringBillId'>[];
}

export interface RecurringVendorBillStats {
  total: number;
  active: number;
  inactive: number;
  dueThisWeek: number;
  dueThisMonth: number;
}

class RecurringVendorBillService {
  private baseUrl = '/accounting/recurring-vendor-bills';

  /**
   * Get all recurring vendor bills
   */
  async getAll(): Promise<RecurringVendorBill[]> {
    const response: any = await apiService.get(this.baseUrl);
    return response.data || [];
  }

  /**
   * Get recurring vendor bill by ID
   */
  async getById(id: string): Promise<RecurringVendorBill> {
    const response: any = await apiService.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Create a new recurring vendor bill
   */
  async create(data: CreateRecurringVendorBillInput): Promise<RecurringVendorBill> {
    const response: any = await apiService.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update an existing recurring vendor bill
   */
  async update(id: string, data: UpdateRecurringVendorBillInput): Promise<RecurringVendorBill> {
    const response: any = await apiService.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a recurring vendor bill
   */
  async delete(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate a recurring vendor bill
   */
  async activate(id: string): Promise<RecurringVendorBill> {
    const response: any = await apiService.post(`${this.baseUrl}/${id}/activate`, {});
    return response.data;
  }

  /**
   * Deactivate a recurring vendor bill
   */
  async deactivate(id: string): Promise<RecurringVendorBill> {
    const response: any = await apiService.post(`${this.baseUrl}/${id}/deactivate`, {});
    return response.data;
  }

  /**
   * Manually generate bills from a recurring template
   */
  async generateBills(id: string): Promise<{ generatedBills: VendorBill[] }> {
    const response: any = await apiService.post(`${this.baseUrl}/${id}/generate`, {});
    return response.data;
  }

  /**
   * Get recurring bills due for generation
   */
  async getDue(): Promise<RecurringVendorBill[]> {
    const response: any = await apiService.get(`${this.baseUrl}/due`);
    return response.data || [];
  }

  /**
   * Get recurring vendor bill statistics
   */
  async getStats(): Promise<RecurringVendorBillStats> {
    const response: any = await apiService.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  /**
   * Process all due recurring bills
   */
  async processAllDue(): Promise<{ processedCount: number; generatedBills: VendorBill[] }> {
    const response: any = await apiService.post(`${this.baseUrl}/process-due`, {});
    return response.data;
  }
}

export const recurringVendorBillService = new RecurringVendorBillService();
export default new VendorBillService();
